import React from "react";
import { object, shape, string, arrayOf, oneOf, any } from "prop-types";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import HeroTitle from "@insightfulscience/atomic-react/Hero/Title";
import UpdatesPageTemplateListItem from "@insightfulscience/atomic-react/UpdatesPageTemplate/Item";
import UpdatesPageTemplatePagination from "@insightfulscience/atomic-react/UpdatesPageTemplate/Pagination";
import Heading from "@insightfulscience/atomic-react/Heading";
import { BackToTopButton } from "@insightfulscience/atomic-react/r2/BackToTopButton";
import PageMeta from "@insightfulscience/smart-react/PageMeta";
import { useLocale } from "@insightfulscience/smart-react/i18";
import NextLink from "@insightfulscience/smart-react/NextLink";
import PagePagination from "@insightfulscience/contentful/ui/PagePagination";
import richTextTypes from "@insightfulscience/contentful/richTextTypes/default";
import { PUBLISHED } from "@insightfulscience/contentful/constants";
import Hero from "@insightfulscience/atomic-react/Hero";
import HeroDescription from "@insightfulscience/atomic-react/Hero/Description";
import Section from "@insightfulscience/atomic-react/Section";
import ContentContainer from "@insightfulscience/atomic-react/r1/atoms/ContentContainer";
import { Col, Row } from "@insightfulscience/atomic-react/Grid";
import ButtonLink from "@insightfulscience/atomic-react/Hero/ButtonLink";
import Icon from "@insightfulscience/atomic-react/Icon";
import Dropdown from "@insightfulscience/atomic-react/Dropdown";
import DropdownMenu from "@insightfulscience/atomic-react/Dropdown/Menu";
import DropdownMenuItem from "@insightfulscience/atomic-react/Dropdown/MenuItem";
import Html from "@insightfulscience/smart-react/Html";
import GuidePageTemplate from "@insightfulscience/atomic-react/GuidePageTemplate";
import GuidePageTemplateMain from "@insightfulscience/atomic-react/GuidePageTemplate/Main";
import GuidePageTemplateSidebar from "@insightfulscience/atomic-react/GuidePageTemplate/Sidebar";
import { GuidePageTemplateNavItem } from "@insightfulscience/atomic-react/GuidePageTemplate/Nav";
import Block from "@insightfulscience/atomic-react/Block";
import {
	PostElementParagraph,
	PostElementTitleH2,
} from "@insightfulscience/atomic-react/PostElement";
import Line from "@insightfulscience/atomic-react/Line";
import { useRouter } from "next/router";

const UpdatesPage = ({ page, pageProps }) => {
	const [t] = useLocale("updates");
	const { siteDomain } = pageProps;
	const { latestVersion = {} } = page;
	const { previousVersions = [] } = page;
	const { pagination = null } = page;
	const { releaseNotes = [] } = page;
	const router = useRouter();
	const selected = router?.query?.select || "";
	const currentPage = router?.query?.page || "";

	return (
		<>
			<PageMeta title={t("META_TITLE")} description={t("META_DESCRIPTION")}>
				<PageMeta.OGP
					title={t("META_TITLE")}
					description={t("META_DESCRIPTION")}
					url={`${siteDomain}/updates/`}
					image={t("META_IMAGE")}
					imageSecureUrl={t("META_IMAGE_SECURE")}
					isImageBaseUrl
				/>
				<PageMeta.Twitter
					card={t("META_TWITTER_CARD")}
					site={t("META_TWITTER_SITE")}
					creator={t("META_TWITTER_CREATOR")}
					title={t("META_TITLE")}
					description={t("META_DESCRIPTION")}
					image={t("META_IMAGE")}
					isImageBaseUrl
				/>
				<PageMeta.Canonical href={`${siteDomain}/updates`} />
				<PageMeta.Custom>
					{(selected || currentPage) && <meta name="robots" content="noindex, follow" />}
				</PageMeta.Custom>
			</PageMeta>
			<Hero color="white" spacing={{ sm: { pb: 10 }, md: { pb: 9 } }}>
				<HeroTitle weight="medium" color="black">
					{t("HERO.TITLE")}
				</HeroTitle>
				<HeroDescription>{t("HERO.DESCRIPTION")}</HeroDescription>
			</Hero>
			<Section utility={{ bgColor: "dotmatics-4" }} spacing={{ py: 10 }}>
				<ContentContainer lg={12} center>
					<Heading sx={{ fontWeight: 600 }} center>
						{t("DOWNLOAD_SECTION.TITLE", { product: latestVersion.title })}
					</Heading>
					<Html Tag="p" className="text-center">
						{t("DOWNLOAD_SECTION.DESCRIPTION", {
							link:
								"https://help.geneious.com/hc/en-us/sections/360009220592-Installing-Geneious-Prime",
						})}
					</Html>
					<Row center sx={{ textAlign: "center" }} spacing={{ mb: 5 }} px py>
						{latestVersion.windowsInstallerUrl && (
							<Col lg="shrink">
								<ButtonLink href={latestVersion.windowsInstallerUrl} styleType="primary">
									<Icon name="windows" />
									{t("DOWNLOAD_SECTION.WIN")}
								</ButtonLink>
							</Col>
						)}
						{latestVersion.macInstallerUrl && (
							<Col lg="shrink">
								<ButtonLink href={latestVersion.macInstallerUrl} styleType="primary">
									<Icon name="mac" /> {t("DOWNLOAD_SECTION.MAC")}
								</ButtonLink>
							</Col>
						)}
						{latestVersion.linuxInstallerUrl && (
							<Col lg="shrink">
								<ButtonLink href={latestVersion.linuxInstallerUrl} styleType="primary">
									<Icon name="linux" /> {t("DOWNLOAD_SECTION.LINUX")}
								</ButtonLink>
							</Col>
						)}
					</Row>
					<Heading Tag="h4" sx={{ fontSize: "m", textAlign: "center", fontWeight: 400 }}>
						{t("DOWNLOAD_SECTION.PREVIOUS_VERSIONS")}
					</Heading>
					<Row center px py utility={{ align: { items: "baseline" } }}>
						<Col md="shrink">Geneious</Col>

						<Col md="shrink">
							<Dropdown>
								<Dropdown.Title
									childrenProps={{
										utility: {
											display: "flex",
											align: { items: "center" },
											border: { bottom: { width: 4, color: 3 } },
										},
										spacing: { px: 2 },
									}}
									triangleProps={{ isOpenColor: "3", size: "xxl", spacing: { ml: 2 } }}
								>
									{latestVersion.version}
								</Dropdown.Title>

								{previousVersions.length && (
									<Dropdown.Body>
										<DropdownMenu>
											{previousVersions.map(({ version, slug }) => (
												<DropdownMenuItem key={slug}>
													<DropdownMenuItem.Link href={`/updates/${slug}`}>
														{version}
													</DropdownMenuItem.Link>
												</DropdownMenuItem>
											))}
										</DropdownMenu>
									</Dropdown.Body>
								)}
							</Dropdown>
						</Col>
					</Row>
				</ContentContainer>
			</Section>
			<Section spacing={{ sm: { py: 10 } }}>
				<GuidePageTemplate>
					<GuidePageTemplateSidebar>
						<GuidePageTemplateNavItem>
							<Block>{t("SELECT_PRODUCT")}:</Block>
						</GuidePageTemplateNavItem>
						<GuidePageTemplateNavItem>
							<NextLink href="?select=prime" className={selected === "prime" ? "active" : null}>
								{t("GENEIOUS_PRIME")}
							</NextLink>
						</GuidePageTemplateNavItem>
						<GuidePageTemplateNavItem>
							<NextLink
								href="?select=biologics"
								className={selected === "biologics" ? "active" : null}
							>
								{t("GENEIOUS_BIOLOGICS")}
							</NextLink>
						</GuidePageTemplateNavItem>
						<GuidePageTemplateNavItem>
							<NextLink href="" className={selected === "" ? "active" : null}>
								{t("ALL_PRODUCTS")}
							</NextLink>
						</GuidePageTemplateNavItem>
					</GuidePageTemplateSidebar>
					<GuidePageTemplateMain>
						<Section>
							<PostElementTitleH2 id="h2_1" spacing={{ mb: 8 }}>
								{t("UPDATES.LATEST_RELEASES")}
							</PostElementTitleH2>
							<Block>
								{releaseNotes.map(item => {
									const releaseDate = new Date(item.fields.releaseDate);

									return (
										<Block key={item.fields.title}>
											<NextLink
												href={`/updates/${item.fields.slug}`}
												sx={{ fontSize: "xxxl", fontWeight: 500 }}
											>
												{t("PRODUCT_NAME")} {item.fields?.appVersionNumber || ""}
											</NextLink>
											<PostElementParagraph sx={{ fontColor: "black--custom-1" }}>
												{t("{{releaseDate, LONGDATE}}", { releaseDate })}
											</PostElementParagraph>
											{item.fields.teaser ? (
												<Block sx={{ fontColor: "black--custom-1" }}>
													{documentToReactComponents(item.fields.teaser, richTextTypes)}
												</Block>
											) : null}
											<NextLink
												Component={UpdatesPageTemplateListItem.Action}
												href={`/updates/${item.fields.slug}`}
											>
												{t("UPDATES.DETAILS")}
											</NextLink>
											<ContentContainer md={8} center>
												<Line spacing={{ my: 10 }} />
											</ContentContainer>
										</Block>
									);
								})}
							</Block>
							{pagination ? (
								<UpdatesPageTemplatePagination>
									<PagePagination {...pagination} />
								</UpdatesPageTemplatePagination>
							) : null}
						</Section>
					</GuidePageTemplateMain>
				</GuidePageTemplate>
			</Section>

			<BackToTopButton />
		</>
	);
};

UpdatesPage.propTypes = {
	page: shape({
		latestVersion: shape({
			version: string,
			majorVersion: string,
			macInstallerUrl: string,
			ubuntuInstallerUrl: string,
			linuxInstallerUrl: string,
		}),
		previousVersions: arrayOf(
			shape({
				slug: string,
				version: string,
				majorVersion: string,
				macInstallerUrl: string,
				ubuntuInstallerUrl: string,
				linuxInstallerUrl: string,
			}),
		),
		releaseNotes: arrayOf(
			shape({
				appReleaseNotes: object, // eslint-disable-line react/forbid-prop-types
				appVersionNumber: string,
				body: object, // eslint-disable-line react/forbid-prop-types
				macInstallerUrl: string,
				publishStatus: oneOf(["Hidden", PUBLISHED]),
				releaseDate: string,
				slug: string,
				teaser: object, // eslint-disable-line react/forbid-prop-types
				title: string,
				windowsInstallerUrl: string,
			}),
		),
	}),
	pageProps: shape({
		resources: any, // eslint-disable-line react/forbid-prop-types
		siteDomain: string.isRequired,
	}),
};

export default UpdatesPage;
